import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Plus,
  Search,
  Edit,
  Trash2,
  ChevronLeft,
  Package,
  AlertCircle,
} from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../lib/supabase'
import Button from '../../components/ui/Button'

export default function AdminProducts() {
  const { isAuthenticated, isAdmin } = useAuth()
  const navigate = useNavigate()
  const [products, setProducts] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/account')
      return
    }
    if (!isAdmin) {
      navigate('/')
      return
    }
    fetchProducts()
  }, [isAuthenticated, isAdmin])

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      setProducts(data || [])
    } catch (error) {
      console.error('Error fetching products:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async (productId) => {
    try {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', productId)

      if (error) throw error

      setProducts(products.filter((p) => p.id !== productId))
      setDeleteConfirm(null)
    } catch (error) {
      console.error('Error deleting product:', error)
      alert('Failed to delete product')
    }
  }

  const updateStock = async (productId, newStock) => {
    try {
      const { error } = await supabase
        .from('products')
        .update({
          stock_quantity: newStock,
          in_stock: newStock > 0,
        })
        .eq('id', productId)

      if (error) throw error

      setProducts(
        products.map((p) =>
          p.id === productId
            ? { ...p, stock_quantity: newStock, in_stock: newStock > 0 }
            : p
        )
      )
    } catch (error) {
      console.error('Error updating stock:', error)
    }
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(price)
  }

  const filteredProducts = products.filter(
    (product) =>
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.category.toLowerCase().includes(searchQuery.toLowerCase())
  )

  if (isLoading) {
    return (
      <div className="min-h-screen bg-pp-ivory flex items-center justify-center">
        <div className="animate-pulse text-pp-stone">Loading products...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-pp-linen">
      <div className="container mx-auto px-4 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 text-pp-stone hover:text-pp-charcoal transition-colors mb-4"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>

          <div className="flex items-center justify-between">
            <h1 className="font-display text-display-md text-pp-ink">
              Products
            </h1>
            <Button as={Link} to="/admin/products/new">
              <Plus className="w-4 h-4" />
              Add Product
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-2xl border border-pp-sand/50 p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-pp-stone" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-pp-sand rounded-lg bg-white text-pp-ink placeholder-pp-stone/60 focus:outline-none focus:ring-2 focus:ring-pp-terracotta/20 focus:border-pp-terracotta transition-colors"
            />
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-white rounded-2xl border border-pp-sand/50 overflow-hidden">
          {filteredProducts.length === 0 ? (
            <div className="p-12 text-center">
              <Package className="w-12 h-12 text-pp-stone mx-auto mb-4" />
              <p className="text-pp-stone">
                {searchQuery ? 'No products found' : 'No products yet'}
              </p>
              {!searchQuery && (
                <Button as={Link} to="/admin/products/new" className="mt-4">
                  Add Your First Product
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-pp-linen/50 border-b border-pp-sand/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-pp-stone">
                      Product
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-pp-stone">
                      Category
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-pp-stone">
                      Price
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-pp-stone">
                      Stock
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-pp-stone">
                      Status
                    </th>
                    <th className="px-6 py-4 text-right text-sm font-medium text-pp-stone">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-pp-sand/30">
                  {filteredProducts.map((product) => (
                    <motion.tr
                      key={product.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-pp-ivory/50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-pp-sand/30 rounded-lg flex-shrink-0 overflow-hidden">
                            {product.images && product.images[0] ? (
                              <img
                                src={product.images[0]}
                                alt={product.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none'
                                  e.target.nextSibling.style.display = 'flex'
                                }}
                              />
                            ) : null}
                            <div
                              className={`w-full h-full flex items-center justify-center ${product.images && product.images[0] ? 'hidden' : ''}`}
                            >
                              <span className="text-pp-stone text-xs">IMG</span>
                            </div>
                          </div>
                          <div>
                            <p className="font-medium text-pp-ink">
                              {product.name}
                            </p>
                            <p className="text-xs text-pp-stone">
                              {product.slug}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="capitalize text-pp-charcoal">
                          {product.category}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-pp-ink">
                          {formatPrice(product.price)}
                        </span>
                        {product.compare_price && (
                          <span className="text-xs text-pp-stone line-through ml-2">
                            {formatPrice(product.compare_price)}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="0"
                            value={product.stock_quantity}
                            onChange={(e) =>
                              updateStock(product.id, parseInt(e.target.value) || 0)
                            }
                            className="w-20 px-2 py-1 border border-pp-sand rounded text-sm text-center"
                          />
                          {product.stock_quantity < 10 && (
                            <AlertCircle className="w-4 h-4 text-yellow-500" />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                            product.in_stock
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {product.in_stock ? 'In Stock' : 'Out of Stock'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            to={`/admin/products/${product.id}`}
                            className="p-2 text-pp-stone hover:text-pp-terracotta transition-colors"
                          >
                            <Edit className="w-4 h-4" />
                          </Link>
                          <button
                            onClick={() => setDeleteConfirm(product.id)}
                            className="p-2 text-pp-stone hover:text-red-500 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/30"
              onClick={() => setDeleteConfirm(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative bg-white rounded-2xl p-6 max-w-md w-full mx-4"
            >
              <h3 className="font-display text-xl text-pp-ink mb-2">
                Delete Product?
              </h3>
              <p className="text-pp-stone mb-6">
                This action cannot be undone. Are you sure you want to delete
                this product?
              </p>
              <div className="flex gap-4">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 px-4 py-2 border border-pp-sand rounded-lg text-pp-charcoal hover:bg-pp-linen transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  )
}
